import React, { useState, useEffect, useMemo } from "react";
import { createPortal } from "react-dom";
import styled, { keyframes } from "styled-components";
import { useNavigate } from "react-router-dom";
import {
  X,
  CornerUpLeft,
  UserPlus,
  User,
  Calendar,
  FileText,
  History,
  Loader2,
  Hash,
  Package,
} from "lucide-react";
import { showSuccessToast, showErrorToast } from "../../../atoms/Utlis/Toast";
import BrandDatePicker from "../../../atoms/Utlis/BrandDatePicker";
import { Equipment } from "./EquipmentConstants";
import { useAppDispatch, useAppSelector } from "../../../../Redux/Hooks";
import { getAllMembers } from "../../../../Redux/ApiCalls/Dashboard/MembersAPI";
import {
  assignEquipment,
  returnEquipment,
  getEquipmentItemDetail,
} from "../../../../Redux/ApiCalls/Dashboard/EquipmentAPI";
import {
  ModalOverlay,
  ModalShell,
  ModalHero,
  HeroGlow,
  HeroRow,
  HeroText,
  HeroBadge,
  ModalTitle,
  ModalSubtitle,
  CloseBtn,
  ModalBody,
  PrimaryBtn,
} from "../../../molecules/Dashboard/EventCalendar/eventModalSharedStyles";
import {
  Field,
  FieldGrid,
  Label,
  Input,
  Select,
  FormLabelIcon,
  FormGradientDefs,
} from "../../../molecules/Dashboard/Member/memberFormStyles";

interface AssignModalProps {
  equipment: Equipment;
  onClose: () => void;
  onUpdate: (updated: Equipment) => void;
}

const spin = keyframes`
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
`;

const Spinner = styled(Loader2)`
  animation: ${spin} 0.8s linear infinite;
`;

const EquipmentCard = styled.div`
  padding: 16px 18px;
  border-radius: 16px;
  background: linear-gradient(135deg, rgba(255, 201, 81, 0.08) 0%, rgba(206, 86, 176, 0.06) 100%);
  border: 1px solid rgba(201, 77, 170, 0.12);
  margin-bottom: 18px;
`;

const EqName = styled.div`
  font-family: Urbanist, sans-serif;
  font-weight: 800;
  font-size: 16px;
  color: #171717;
`;

const EqMeta = styled.div`
  font-family: Montserrat, sans-serif;
  font-size: 12px;
  color: rgba(23, 23, 23, 0.55);
  margin-top: 4px;
`;

const QtyGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 8px;
  margin-top: 12px;
  @media (max-width: 520px) {
    grid-template-columns: 1fr;
  }
`;

const QtyChip = styled.div`
  padding: 10px 12px;
  border-radius: 12px;
  background: rgba(255, 255, 255, 0.85);
  border: 1px solid rgba(23, 23, 23, 0.06);
`;

const QtyLabel = styled.div`
  font-family: Montserrat, sans-serif;
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  color: rgba(23, 23, 23, 0.45);
`;

const QtyValue = styled.div`
  font-family: Urbanist, sans-serif;
  font-size: 18px;
  font-weight: 800;
  color: #171717;
  margin-top: 2px;
`;

const ModeTabs = styled.div`
  display: flex;
  gap: 8px;
  margin-bottom: 16px;
`;

const ModeTab = styled.button<{ $active: boolean }>`
  flex: 1;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  min-height: 40px;
  border-radius: 12px;
  border: 1px solid ${(p) => (p.$active ? "transparent" : "rgba(23, 23, 23, 0.1)")};
  background: ${(p) =>
    p.$active
      ? "linear-gradient(144.01deg, rgba(255, 201, 81, 0.9) 0%, rgba(206, 86, 176, 0.9) 100%)"
      : "#fafafa"};
  color: ${(p) => (p.$active ? "#fff" : "rgba(23, 23, 23, 0.7)")};
  font-family: Montserrat, sans-serif;
  font-size: 12px;
  font-weight: 700;
  cursor: pointer;
`;

const FormWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const SerialPicker = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  max-height: 180px;
  overflow-y: auto;
  padding: 4px;
`;

const SerialOption = styled.label<{ $checked: boolean }>`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 12px;
  border-radius: 12px;
  border: 1px solid ${(p) => (p.$checked ? "rgba(201, 77, 170, 0.35)" : "rgba(23, 23, 23, 0.08)")};
  background: ${(p) => (p.$checked ? "rgba(201, 77, 170, 0.06)" : "#fafafa")};
  cursor: pointer;
  font-family: Montserrat, sans-serif;
  font-size: 13px;
  font-weight: 600;
  color: #171717;

  input {
    width: 16px;
    height: 16px;
    accent-color: #c94daa;
  }
`;

const HintText = styled.p`
  margin: 0;
  font-family: Montserrat, sans-serif;
  font-size: 12px;
  color: rgba(23, 23, 23, 0.5);
`;

const SecondaryBtn = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  width: 100%;
  min-height: 44px;
  margin-top: 16px;
  border-radius: 12px;
  border: 1px solid rgba(23, 23, 23, 0.1);
  background: #fff;
  color: rgba(23, 23, 23, 0.75);
  font-family: Montserrat, sans-serif;
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;
`;

type Mode = "assign" | "return";

const AssignEquipmentModal: React.FC<AssignModalProps> = ({ equipment, onClose, onUpdate }) => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { members, isMemberFetched } = useAppSelector((state) => state.member);
  const { selectedBranch } = useAppSelector((state) => state.auth);

  const [liveEq, setLiveEq] = useState(equipment);
  const [mode, setMode] = useState<Mode>(
    equipment.remainingTotal > 0 ? "assign" : "return"
  );
  const [selectedMemberId, setSelectedMemberId] = useState(equipment.assignedMemberId || "");
  const [assignDate, setAssignDate] = useState(
    equipment.assignmentDate || new Date().toISOString().split("T")[0]
  );
  const [returnDate, setReturnDate] = useState(equipment.expectedReturnDate || "");
  const [remarks, setRemarks] = useState("");
  const [returnRemarks, setReturnRemarks] = useState("");
  const [assignQty, setAssignQty] = useState("1");
  const [selectedSerials, setSelectedSerials] = useState<string[]>([]);
  const [returnSerials, setReturnSerials] = useState<string[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [loadingDetail, setLoadingDetail] = useState(true);

  useEffect(() => {
    const branchId = equipment.branch_id || selectedBranch?.id || null;
    dispatch(getAllMembers(branchId));
  }, [equipment.branch_id, selectedBranch?.id, dispatch]);

  useEffect(() => {
    const load = async () => {
      setLoadingDetail(true);
      try {
        const detail = await getEquipmentItemDetail(equipment.id);
        const totalQty = detail.total_quantity ?? detail.quantity ?? 1;
        const totalAssigned = detail.total_assigned ?? 0;
        const remaining = detail.remaining_total ?? totalQty - totalAssigned;
        const available =
          detail.available_serial_numbers ||
          detail.items?.filter((u) => u.status === "available" && u.serial_number).map((u) => u.serial_number!) ||
          [];
        const assigned =
          detail.assigned_serial_numbers ||
          detail.items?.filter((u) => u.status === "assigned" && u.serial_number).map((u) => u.serial_number!) ||
          [];
        setLiveEq({
          ...equipment,
          quantity: totalQty,
          totalQuantity: totalQty,
          totalAssigned,
          remainingTotal: remaining,
          availableSerialNumbers: available,
          assignedSerialNumbers: assigned,
          units:
            detail.items?.map((u) => ({
              id: u.id,
              serialNumber: u.serial_number || "",
              status: u.status,
              assignment: u.assignment,
            })) || [],
          assignedMember: detail.active_assignment?.team_member_name || equipment.assignedMember,
          assignedMemberId: detail.active_assignment?.team_member
            ? String(detail.active_assignment.team_member)
            : equipment.assignedMemberId,
          assignmentDate: detail.active_assignment?.assigned_at
            ? detail.active_assignment.assigned_at.split("T")[0]
            : equipment.assignmentDate,
          expectedReturnDate: detail.active_assignment?.expected_return_at
            ? detail.active_assignment.expected_return_at.split("T")[0]
            : equipment.expectedReturnDate,
        });
        if (remaining <= 0 && totalAssigned > 0) setMode("return");
        else if (remaining > 0) setMode("assign");
      } catch {
        /* keep list data */
      } finally {
        setLoadingDetail(false);
      }
    };
    load();
  }, [equipment.id]);

  const canAssign = liveEq.remainingTotal > 0 && liveEq.status !== "Sold" && liveEq.status !== "Retired";
  const canReturn = liveEq.totalAssigned > 0;

  const availableOptions = useMemo(() => {
    const withSerial = liveEq.availableSerialNumbers.filter(Boolean);
    if (withSerial.length > 0) return withSerial;
    return liveEq.units
      .filter((u) => u.status === "available")
      .map((u) => u.serialNumber || `Unit #${u.id}`);
  }, [liveEq]);

  const assignedOptions = useMemo(() => {
    const withSerial = liveEq.assignedSerialNumbers.filter(Boolean);
    if (withSerial.length > 0) return withSerial;
    return liveEq.units
      .filter((u) => u.status === "assigned")
      .map((u) => u.serialNumber || `Unit #${u.id}`);
  }, [liveEq]);

  const hasNamedAvailable = liveEq.availableSerialNumbers.some(Boolean);
  const hasNamedAssigned = liveEq.assignedSerialNumbers.some(Boolean);

  const toggleSerial = (sn: string, list: string[], setter: (v: string[]) => void) => {
    setter(list.includes(sn) ? list.filter((s) => s !== sn) : [...list, sn]);
  };

  const handleAssign = async () => {
    if (!selectedMemberId) {
      showErrorToast("Please select a team member");
      return;
    }
    if (!returnDate) {
      showErrorToast("Please select expected return date");
      return;
    }

    const payload: Parameters<typeof assignEquipment>[1] = {
      team_member_id: Number(selectedMemberId),
      expected_return_at: returnDate,
      remarks: remarks || undefined,
    };

    if (hasNamedAvailable && selectedSerials.length > 0) {
      payload.serial_numbers = selectedSerials;
    } else if (!hasNamedAvailable) {
      const qty = Number(assignQty) || 1;
      if (qty < 1 || qty > liveEq.remainingTotal) {
        showErrorToast(`Quantity must be between 1 and ${liveEq.remainingTotal}`);
        return;
      }
      payload.quantity = qty;
    } else if (selectedSerials.length === 0) {
      payload.quantity = 1;
    }

    setSubmitting(true);
    try {
      await assignEquipment(equipment.id, payload);
      showSuccessToast("Equipment successfully assigned");
      onUpdate(null as any);
    } catch (err: any) {
      showErrorToast(err?.message || "Failed to assign equipment");
    } finally {
      setSubmitting(false);
    }
  };

  const handleReturn = async () => {
    const payload: Parameters<typeof returnEquipment>[1] = {
      remarks: returnRemarks || undefined,
    };
    if (hasNamedAssigned && returnSerials.length > 0) {
      payload.serial_numbers = returnSerials;
    }

    setSubmitting(true);
    try {
      await returnEquipment(equipment.id, payload);
      showSuccessToast(
        returnSerials.length > 0
          ? "Selected units returned to inventory"
          : "Equipment returned to inventory"
      );
      onUpdate(null as any);
    } catch (err: any) {
      showErrorToast(err?.message || "Failed to return equipment");
    } finally {
      setSubmitting(false);
    }
  };

  return createPortal(
    <>
      <FormGradientDefs />
      <ModalOverlay onClick={onClose}>
        <ModalShell style={{ maxWidth: 560 }} onClick={(e) => e.stopPropagation()}>
          <ModalHero>
            <HeroGlow />
            <HeroRow>
              <HeroText>
                <HeroBadge>
                  {mode === "return" ? <CornerUpLeft size={11} /> : <UserPlus size={11} />}
                  {mode === "return" ? "Return gear" : "Assign gear"}
                </HeroBadge>
                <ModalTitle>
                  {mode === "return" ? "Return Equipment" : "Assign Equipment"}
                </ModalTitle>
                <ModalSubtitle>
                  {mode === "return"
                    ? "Return specific serials or all units on the open assignment."
                    : "Assign available units by serial or quantity."}
                </ModalSubtitle>
              </HeroText>
              <CloseBtn type="button" onClick={onClose} aria-label="Close">
                <X size={18} />
              </CloseBtn>
            </HeroRow>
          </ModalHero>

          <ModalBody>
            <EquipmentCard>
              <EqName>{liveEq.name}</EqName>
              <EqMeta>
                {liveEq.brand}
                {liveEq.model ? ` · ${liveEq.model}` : ""}
              </EqMeta>
              <QtyGrid>
                <QtyChip>
                  <QtyLabel>Total Quantity</QtyLabel>
                  <QtyValue>{loadingDetail ? "…" : liveEq.totalQuantity}</QtyValue>
                </QtyChip>
                <QtyChip>
                  <QtyLabel>Total Assigned</QtyLabel>
                  <QtyValue>{loadingDetail ? "…" : liveEq.totalAssigned}</QtyValue>
                </QtyChip>
                <QtyChip>
                  <QtyLabel>Remaining</QtyLabel>
                  <QtyValue>{loadingDetail ? "…" : liveEq.remainingTotal}</QtyValue>
                </QtyChip>
              </QtyGrid>
            </EquipmentCard>

            {canAssign && canReturn && (
              <ModeTabs>
                <ModeTab type="button" $active={mode === "assign"} onClick={() => setMode("assign")}>
                  <UserPlus size={14} /> Assign
                </ModeTab>
                <ModeTab type="button" $active={mode === "return"} onClick={() => setMode("return")}>
                  <CornerUpLeft size={14} /> Return
                </ModeTab>
              </ModeTabs>
            )}

            {mode === "return" && canReturn ? (
              <FormWrapper>
                {liveEq.assignedMember && (
                  <HintText>
                    Latest assignment: <strong>{liveEq.assignedMember}</strong>
                    {liveEq.expectedReturnDate ? ` · Due ${liveEq.expectedReturnDate}` : ""}
                  </HintText>
                )}
                {hasNamedAssigned ? (
                  <Field>
                    <Label>
                      <FormLabelIcon icon={Hash} />
                      Serials to return (optional — leave empty to return all)
                    </Label>
                    <SerialPicker>
                      {assignedOptions.map((sn) => (
                        <SerialOption key={sn} $checked={returnSerials.includes(sn)}>
                          <input
                            type="checkbox"
                            checked={returnSerials.includes(sn)}
                            onChange={() => toggleSerial(sn, returnSerials, setReturnSerials)}
                          />
                          {sn}
                        </SerialOption>
                      ))}
                    </SerialPicker>
                  </Field>
                ) : (
                  <HintText>
                    {liveEq.totalAssigned} unit(s) assigned — returning will free them without serial selection.
                  </HintText>
                )}
                <Field>
                  <Label htmlFor="return-remarks">
                    <FormLabelIcon icon={FileText} />
                    Remarks / Return Notes
                  </Label>
                  <Input
                    id="return-remarks"
                    placeholder="e.g. Returned in good condition"
                    value={returnRemarks}
                    onChange={(e) => setReturnRemarks(e.target.value)}
                  />
                </Field>
                <PrimaryBtn type="button" onClick={handleReturn} disabled={submitting}>
                  {submitting ? <Spinner size={16} /> : <CornerUpLeft size={16} />}
                  {submitting
                    ? "Processing..."
                    : returnSerials.length > 0
                      ? `Return ${returnSerials.length} Unit(s)`
                      : "Return All Assigned Units"}
                </PrimaryBtn>
              </FormWrapper>
            ) : canAssign ? (
              <FormWrapper>
                <Field>
                  <Label htmlFor="assign-member">
                    <FormLabelIcon icon={User} />
                    Assign to Team Member
                  </Label>
                  <Select
                    id="assign-member"
                    value={selectedMemberId}
                    onChange={(e) => setSelectedMemberId(e.target.value)}
                  >
                    <option value="">Select Member...</option>
                    {members.map((m) => (
                      <option key={m.id} value={m.id}>
                        {m.name} ({m.job_type || "Crew"})
                      </option>
                    ))}
                  </Select>
                </Field>

                {hasNamedAvailable ? (
                  <Field>
                    <Label>
                      <FormLabelIcon icon={Hash} />
                      Available serials ({liveEq.remainingTotal} remaining)
                    </Label>
                    <HintText style={{ marginBottom: 8 }}>
                      Select specific serials, or leave empty to assign 1 available unit.
                    </HintText>
                    <SerialPicker>
                      {availableOptions.map((sn) => (
                        <SerialOption key={sn} $checked={selectedSerials.includes(sn)}>
                          <input
                            type="checkbox"
                            checked={selectedSerials.includes(sn)}
                            onChange={() => toggleSerial(sn, selectedSerials, setSelectedSerials)}
                          />
                          {sn}
                        </SerialOption>
                      ))}
                    </SerialPicker>
                  </Field>
                ) : (
                  <Field>
                    <Label htmlFor="assign-qty">
                      <FormLabelIcon icon={Package} />
                      Quantity to assign (max {liveEq.remainingTotal})
                    </Label>
                    <Input
                      id="assign-qty"
                      type="number"
                      min={1}
                      max={liveEq.remainingTotal}
                      value={assignQty}
                      onChange={(e) => setAssignQty(e.target.value)}
                    />
                  </Field>
                )}

                <FieldGrid>
                  <Field>
                    <Label htmlFor="assign-date">
                      <FormLabelIcon icon={Calendar} />
                      Handout Date
                    </Label>
                    <BrandDatePicker
                      name="assign-date"
                      value={assignDate}
                      onChange={(v) => setAssignDate(v)}
                    />
                  </Field>
                  <Field>
                    <Label htmlFor="return-date">
                      <FormLabelIcon icon={Calendar} />
                      Expected Return
                    </Label>
                    <BrandDatePicker
                      name="return-date"
                      value={returnDate}
                      minDate={assignDate}
                      onChange={(v) => setReturnDate(v)}
                    />
                  </Field>
                </FieldGrid>
                <Field>
                  <Label htmlFor="assign-remarks">
                    <FormLabelIcon icon={FileText} />
                    Remarks / Purpose
                  </Label>
                  <Input
                    id="assign-remarks"
                    placeholder="e.g. Wedding shoot project"
                    value={remarks}
                    onChange={(e) => setRemarks(e.target.value)}
                  />
                </Field>
                <PrimaryBtn type="button" onClick={handleAssign} disabled={submitting}>
                  {submitting ? <Spinner size={16} /> : <UserPlus size={16} />}
                  {submitting
                    ? "Assigning..."
                    : selectedSerials.length > 0
                      ? `Assign ${selectedSerials.length} Unit(s)`
                      : "Assign Equipment"}
                </PrimaryBtn>
              </FormWrapper>
            ) : (
              <HintText>No units available to assign or return for this equipment.</HintText>
            )}

            <SecondaryBtn
              type="button"
              onClick={() => {
                onClose();
                navigate(`/dashboard/equipment/${equipment.id}/history`);
              }}
            >
              <History size={15} /> View assignment history
            </SecondaryBtn>
          </ModalBody>
        </ModalShell>
      </ModalOverlay>
    </>,
    document.body
  );
};

export default AssignEquipmentModal;
